import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcSlideToggleComponent, SvcSlideToggleModule } from '@solvace/component/svc-slide-toggle';
import { SvcToastService } from '@solvace/component/svc-toast';
import { HttpErrorService, LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';
import { EMPTY, filter, map, switchMap, take, timer } from 'rxjs';
import { OutlookIntegrationApiService } from './services/outlook-integration-api';
import { OutlookConnectionResult, OutlookIntegrationStatus } from './models/outlook-integration.types';

export const OUTLOOK_INTEGRATION_MESSAGES = {
  sectionTitle: 'Integrações',
  toggleLabel: 'Integração com Outlook',
  consentTitle: 'Conectar ao Outlook',
  consentBody:
    "Ao ativar a integração com o Outlook, dados dos seus Planos de Ação (identificador, título, data de vencimento, situação e link de acesso) serão enviados para sua conta Microsoft conectada. O Solvace criará um calendário próprio chamado 'Solvace' na sua conta, além de categorias de cor usadas para identificar a situação de cada Plano de Ação, e gerenciará apenas os eventos desse calendário. Mais detalhes na nossa Política de Privacidade.",
  consentConfirm: 'Conectar ao Outlook',
  consentCancel: 'Cancelar',
  connectionCancelled: 'Conexão com o Outlook cancelada.',
  connectionError: 'Não foi possível conectar ao Outlook. Tente novamente em alguns instantes.',
  calendarError: 'Não foi possível criar seu calendário Solvace no Outlook. Tente novamente em alguns instantes.',
  activated: 'Integração com Outlook ativada. Seus Planos de Ação serão sincronizados em segundo plano.',
  personalAccountRejected: 'Apenas contas Microsoft 365 corporativas são suportadas no momento. Use sua conta corporativa para ativar a integração.',
  syncPromptTitle: 'Sincronizar seus Planos de Ação atuais?',
  syncPromptBody: 'Você tem {{count}} Planos de Ação em aberto sob sua responsabilidade. Deseja criá-los como eventos no seu calendário Solvace?',
  syncPromptConfirm: 'Sim, sincronizar todos',
  syncPromptCancel: 'Não, apenas sincronizar novos PLAs',
  syncStarted: 'Sincronizando {{count}} Planos de Ação em segundo plano.',
  expired: 'Sua conexão com o Outlook expirou. Reative a integração para continuar sincronizando seus Planos de Ação.',
  deactivateTitle: 'Desativar integração com Outlook',
  deactivateBody:
    "Ao desativar, o calendário 'Solvace', todos os seus eventos e as categorias de cor criadas pelo Solvace serão removidos da sua conta Outlook. Esta ação não pode ser desfeita.",
  deactivateConfirm: 'Desativar',
  deactivateCancel: 'Cancelar',
  deactivated: 'Integração com Outlook desativada. O calendário Solvace foi removido da sua conta.',
  deactivatedCalendarNotRemoved:
    'Integração com Outlook desativada. Não foi possível remover o calendário Solvace do seu Outlook automaticamente — você pode removê-lo manualmente se desejar.',
} as const;

export type OutlookToggleStatus = 'loading' | 'off' | 'connecting' | 'on' | 'deactivating';

export const OUTLOOK_CONNECTION_POLL_INTERVAL_MS = 2000;
const CONNECTION_POLL_MAX_ATTEMPTS = 150;
const POPUP_CLOSED_GRACE_POLLS = 2;
const POPUP_NAME = 'solvace-outlook-oauth';
const POPUP_FEATURES = 'popup=yes,width=600,height=720';

@Component({
  selector: 'svc-outlook-integration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LibSharedCommonModule, TranslocoModule, MatIconModule, MatProgressSpinnerModule, SvcSlideToggleModule],
  templateUrl: './outlook-integration.html',
})
export class OutlookIntegrationComponent implements OnInit {
  private readonly _api = inject(OutlookIntegrationApiService);
  private readonly _dialogService = inject(SvcDialogService);
  private readonly _toastService = inject(SvcToastService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _httpErrorService = inject(HttpErrorService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _toggle = viewChild(SvcSlideToggleComponent);

  protected readonly messages = OUTLOOK_INTEGRATION_MESSAGES;
  public readonly status = signal<OutlookToggleStatus>('loading');
  public readonly expiredNotice = signal(false);
  protected readonly toggleChecked = computed(() => {
    const status = this.status();
    return status === 'on' || status === 'connecting' || status === 'deactivating';
  });
  protected readonly processing = computed(() => {
    const status = this.status();
    return status === 'connecting' || status === 'deactivating';
  });
  protected readonly toggleDisabled = computed(() => this.status() === 'loading' || this.processing());

  public ngOnInit(): void {
    this._api
      .getStatus()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (state) => {
          if (state.status === OutlookIntegrationStatus.Active) {
            this.status.set('on');
          } else {
            this.status.set('off');
            this.expiredNotice.set(state.status === OutlookIntegrationStatus.Expired);
          }
        },
        error: (error) => {
          this.status.set('off');
          this._httpErrorService.showErrorInToast(error);
        },
      });
  }

  public onToggleChange(event: MatSlideToggleChange): void {
    if (event.checked && this.status() === 'off') {
      this.beginActivation();
    } else if (!event.checked && this.status() === 'on') {
      this.confirmDeactivation();
    }
  }

  private beginActivation(): void {
    this.status.set('connecting');
    this._dialogService
      .openConfirm(this.messages.consentBody, {
        title: this.messages.consentTitle,
        okText: this.messages.consentConfirm,
        cancelText: this.messages.consentCancel,
      })
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          this.setOff();
          return;
        }
        this.launchOAuthPopup();
      });
  }

  private launchOAuthPopup(): void {
    const popup = globalThis.open(this._api.oauthStartUrl, POPUP_NAME, POPUP_FEATURES);
    if (!popup) {
      this.failActivation(this.messages.connectionError);
      return;
    }
    this.pollConnection(popup);
  }

  private pollConnection(popup: Window): void {
    let attempts = 0;
    let pollsAfterPopupClosed = 0;
    timer(0, OUTLOOK_CONNECTION_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this._api.getConnectionStatus()),
        map((connection) => {
          if (connection.result !== OutlookConnectionResult.Pending) return connection.result;
          attempts++;
          if (popup.closed) pollsAfterPopupClosed++;
          if (pollsAfterPopupClosed > POPUP_CLOSED_GRACE_POLLS) return OutlookConnectionResult.Cancelled;
          if (attempts > CONNECTION_POLL_MAX_ATTEMPTS) return OutlookConnectionResult.Error;
          return OutlookConnectionResult.Pending;
        }),
        filter((result) => result !== OutlookConnectionResult.Pending),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (result) => this.handleConnectionResult(result, popup),
        error: () => {
          this.closePopup(popup);
          this.failActivation(this.messages.connectionError);
        },
      });
  }

  private handleConnectionResult(result: OutlookConnectionResult, popup: Window): void {
    this.closePopup(popup);
    switch (result) {
      case OutlookConnectionResult.Connected:
        this.completeActivation();
        break;
      case OutlookConnectionResult.Cancelled:
        this.setOff();
        this._toastService.warning(this._translocoService.translate(this.messages.connectionCancelled));
        break;
      case OutlookConnectionResult.PersonalAccountRejected:
        this.failActivation(this.messages.personalAccountRejected);
        break;
      case OutlookConnectionResult.CalendarError:
        this.failActivation(this.messages.calendarError);
        break;
      default:
        this.failActivation(this.messages.connectionError);
    }
  }

  private completeActivation(): void {
    this.status.set('on');
    this.expiredNotice.set(false);
    this._toastService.success(this._translocoService.translate(this.messages.activated));
    this.promptInitialSync();
  }

  private promptInitialSync(): void {
    this._api
      .getEligibleActionPlans()
      .pipe(
        switchMap(({ count }) => {
          if (count <= 0) return EMPTY;
          const body = this._translocoService.translate(this.messages.syncPromptBody, { count });
          return this._dialogService
            .openConfirm(body, {
              title: this.messages.syncPromptTitle,
              okText: this.messages.syncPromptConfirm,
              cancelText: this.messages.syncPromptCancel,
            })
            .pipe(
              take(1),
              switchMap((confirmed) => {
                if (confirmed !== true) return EMPTY;
                return this._api.syncHistory().pipe(map(() => count));
              })
            );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (count) => this._toastService.success(this._translocoService.translate(this.messages.syncStarted, { count })),
        error: (error) => this._httpErrorService.showErrorInToast(error),
      });
  }

  private confirmDeactivation(): void {
    this._dialogService
      .openConfirm(this.messages.deactivateBody, {
        title: this.messages.deactivateTitle,
        okText: this.messages.deactivateConfirm,
        cancelText: this.messages.deactivateCancel,
      })
      .pipe(
        take(1),
        switchMap((confirmed) => {
          if (confirmed !== true) {
            this.restoreOn();
            return EMPTY;
          }
          this.status.set('deactivating');
          return this._api.deactivate();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (result) => {
          this.setOff();
          const message = result.calendarRemoved ? this.messages.deactivated : this.messages.deactivatedCalendarNotRemoved;
          if (result.calendarRemoved) {
            this._toastService.success(this._translocoService.translate(message));
          } else {
            this._toastService.warning(this._translocoService.translate(message));
          }
        },
        error: (error) => {
          this.restoreOn();
          this._httpErrorService.showErrorInToast(error);
        },
      });
  }

  private failActivation(message: string): void {
    this.setOff();
    this._toastService.error(this._translocoService.translate(message));
  }

  private setOff(): void {
    this.status.set('off');
    this.syncToggleVisual(false);
  }

  private restoreOn(): void {
    this.status.set('on');
    this.syncToggleVisual(true);
  }

  private syncToggleVisual(checked: boolean): void {
    const toggle = this._toggle();
    if (!toggle) return;
    toggle.checked = checked;
    if (toggle.slideToggle) {
      toggle.slideToggle.checked = checked;
    }
  }

  private closePopup(popup: Window): void {
    if (!popup.closed) {
      popup.close();
    }
  }
}
